import { defineStore } from 'pinia'

const images = import.meta.globEager('@/assets/images/**/*.{png,jpg,jpeg,svg,webp,gif,ico}')
const lotties = import.meta.globEager('@/assets/lotties/**/*')

function findLottieKey(obj, searchString) {
  return Object.keys(obj).find((key) => key.includes(searchString))
}

function findImageKey(obj, searchString) {
  return Object.keys(obj).find((key) => key.includes(searchString))
}

export const useSiteConfigStore = defineStore('siteConfig', {
  state: () => ({
    canSelectOffice: false,
    homeCarouselWithProductsBrands: false,
    clientFormatDataOnSearch: ['name', 'client_num', 'price_id'],
    brandImagesShared: false,
    b2c: false,
    whatsappNumber: '',
    brandName: '',
    titleBase: '',
    onlyQuotations: false,
    minimumPurchasePrice: 1500,
    displayWholesalePrices: false,
    displayProductPageCatalog: false,
    displayProductReleaseDate: false,
    productConfig: false,
    assetsFolderPrefix: '',
    colors: {},
    pricingRules: {},
    shoppingOptions: {},
    registerConfig: {},
    userLocationLogic: false
  }),
  getters: {
    getCanSelectOffice: (state) => state.canSelectOffice,
    getHomeCarouselWithProductsBrands: (state) => state.homeCarouselWithProductsBrands,
    getClientFormatDataOnSearch: (state) => state.clientFormatDataOnSearch,
    getBrandImagesShared: (state) => state.brandImagesShared,
    getB2C: (state) => state.b2c,
    getUserLocationLogic: (state) => state.userLocationLogic,
    getRegisterConfig: (state) => state.registerConfig,
    getWhatsAppLink: (state) => {
      if (!state.whatsappNumber) {
        return false
      }
      const whatsappLink = `https://api.whatsapp.com/send?phone=${state.whatsappNumber}`
      return whatsappLink
    },
    getShoppingOptions: (state) => state.shoppingOptions,
    getPriceRuleMessageSuccess: (state) => {
      const message =
        state.pricingRules.successMessage ||
        '¡Felicidades! Tu carrito supera los <strong>##price</strong>, accedes a precios de fábrica. ¡Disfruta tus ahorros!'
      return message.replace('##price', `$${state.pricingRules.thresholdAmountForTierUpgrade}`)
    },
    getPriceRuleEnabled: (state) => state.priceRules.enableDynamicTierPricing,
    getPriceThresholdAmount: (state) => state.priceRules.thresholdAmountForTierUpgrade,
    getPriceSwitchTo: (state) => state.priceRules.toPriceKey,
    getExcludedUserByProfileKey: (state) => state.priceRules.excludedUserByProfileKey,
    getExcludedUserTypes: (state) => state.priceRules.excludedUserTypes,
    getPricingRules: (state) => state.pricingRules,
    getDisplayProductPageCatalog: (state) => state.displayProductPageCatalog,
    getDisplayProductReleaseDate: (state) => state.displayProductReleaseDate,
    getProductConfigImage: (state) => state.productConfig?.visualConfigImage,
    getProductConfigIcon: (state) => state.productConfig?.visualConfigIcons,
    getBrandName: (state) => state.brandName,
    getBaseTitle: (state) => state.titleBase,
    getMinimumPurchasePrice: (state) => state.minimumPurchasePrice,
    getDisplayWholesalePrices: (state) => state.displayWholesalePrices,
    getColors: (state) => state.colors,
    getPrimaryColor: (state) => state.colors['primary'],
    getAssetsFolderPrefix: (state) => state.assetsFolderPrefix,
    getOnlyQuotations: (state) => state.onlyQuotations
  },
  actions: {
    setMinimunPurchasePrice(value) {
      if (!value || isNaN(value)) {
        console.error('El valor de minimumPurchasePrice no es válido:', value)
        return
      }
      this.minimumPurchasePrice = value
    },
    setSiteConfig(config) {
      if (config.minimumPurchasePrice && isNaN(config.minimumPurchasePrice)) {
        console.error('El valor de minimumPurchasePrice no es válido:', config.minimumPurchasePrice)
        config.minimumPurchasePrice = 1500 // Valor por defecto
      }
      Object.assign(this, config)
    },
    getImagePath(fileName, isDefaultActive = false) {
      return `/images/${isDefaultActive ? 'default' : this.getAssetsFolderPrefix}/${fileName}`
    },
    getImageSharedPath(fileName) {
      return `/images/shared/${fileName}`
    },
    getLottiePath(fileName, isDefaultActive = false) {
      return `/assets/lotties/${isDefaultActive ? 'default' : this.getAssetsFolderPrefix}/${fileName}.json`
    },
    getLottieSharedPath(fileName) {
      return `/assets/lotties/shared/${fileName}.json`
    },
    async findLottie(fileName, shared = false) {
      const lottiePath = shared ? this.getLottieSharedPath(fileName) : this.getLottiePath(fileName)
      let matchedKey = findLottieKey(lotties, lottiePath)

      if (matchedKey) {
        return lotties[matchedKey].default // La ruta de la imagen será resuelta aquí
      } else {
        console.error(`Lottie no encontrado: ${lottiePath}`)
        const defaultLottieModule = this.getImagePath(fileName, true)
        matchedKey = findLottieKey(lotties, defaultLottieModule)
        return lotties[matchedKey]?.default || null
      }
    },
    async findImage(fileName, shared = false) {
      const imagePath = shared ? this.getImageSharedPath(fileName) : this.getImagePath(fileName)
      let matchedKey = findImageKey(images, imagePath)
      if (matchedKey) {
        return images[matchedKey].default // La ruta de la imagen será resuelta aquí
      } else {
        const defaultImagePath = this.getImagePath(fileName, true)
        matchedKey = findImageKey(images, defaultImagePath)
        console.error(`Imagen no encontrada: ${imagePath}`)
        return images[matchedKey]?.default || null
      }
    }
  },
  persist: true
})
